import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Avatar,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Card,
  CardContent,
  Grid,
  Tooltip,
  TableFooter,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Badge,
  Tab,
  Tabs,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Snackbar,
} from "@mui/material";
import {
  Search,
  Close,
  Person,
  Payment,
  Warning,
  Bed,
  CalendarToday,
  Phone,
  Email,
  Cancel,
  Delete,
  Hotel,
  MeetingRoom,
  AccessTime,
  CheckCircle,
  DateRange,
  Login,
  Logout,
  Bedtime,
  Visibility,
  Refresh,
  Receipt,
  AccountBalance,
  CreditCard,
  Business,
  Home,
  Description,
  AttachMoney,
  Event,
  PersonPin,
  Badge as BadgeIcon,
  Room,
  Schedule,
} from "@mui/icons-material";
import { formatCurrencyN } from "../../../common/utils_t";
import {
  getCustomersOrders_checkInCheckOut,
} from "../../../api/customers/customers";
import { IonApp, IonButton, IonContent, IonPage, useIonViewWillEnter, IonRefresher, IonRefresherContent } from "@ionic/react";
import axios from "axios";
import { baseUrl, config, requestID } from "../../../common/utils";
import { checkOutCustomers } from "../../../api/orders/orders";

// Room item interface based on your actual API response
interface RoomItem {
  id: string;
  order_id: string;
  product_id: string;
  quantity: number;
  price: number;
  total_amount: number;
  description: string;
  checked_in: number; // 0 or 1
  checked_out: number; // 0 or 1
  check_in_date: string;
  check_out_date: string;
  date_bags: string;
  customer_name: string;
  customer_phone: string;
  customer_email: string;
  staff_name: string;
  status: string;
  created_at: string;
  last_updated: string;
  member_id: string;
  branch_id: string;
  business_id: string;
  room_name: string;
  // Additional fields
  cost_price?: string;
  deleted_at?: string | null;
  month_id?: string;
  roleID?: string;
  updated_at?: string | null;
}

// Check-In/Out Dialog props
interface CheckDialogProps {
  open: boolean;
  onClose: () => void;
  roomItem: RoomItem | null;
  onConfirm: (notes?: string) => void;
  loading: boolean;
  actionType: 'checkin' | 'checkout';
  onSuccess: () => void; // Add this callback for successful action
}

// View Details Dialog props
interface ViewDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  roomItem: RoomItem | null;
}

const CheckInOutDialog: React.FC<CheckDialogProps> = ({
  open,
  onClose,
  roomItem,
  onConfirm,
  loading,
  actionType,
  onSuccess
}) => {
  const [notes, setNotes] = useState("");

  const getActionTitle = () => {
    return actionType === 'checkin' ? 'Check-In Room' : 'Check-Out Room';
  };

  const getActionButtonText = () => {
    return actionType === 'checkin' ? 'Check-In' : 'Check-Out';
  };

  const handleConfirm = async () => {
    try {
      // For checkout, use the direct API call
      if (actionType === 'checkout' && roomItem?.order_id) {
        const response = await checkOutCustomers(roomItem.order_id);
        console.log("Check-out response:", response);
        
        // Trigger success callback to refresh parent component
        onSuccess();
        
        // Close the dialog
        onClose();
      } else {
        // For check-in, use the original onConfirm
        onConfirm(notes.trim() || undefined);
      }
    } catch (error) {
      console.error("Error during check-out:", error);
    }
  };

  const formatDate = (dateString: string) => {
    try {
      if (!dateString || dateString === "0000-00-00 00:00:00") {
        return "Not set";
      }
      return new Date(dateString).toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch (error) {
      return "Invalid date";
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ 
        bgcolor: `${actionType === 'checkin' ? 'success' : 'warning'}.light`, 
        color: `${actionType === 'checkin' ? 'success' : 'warning'}.contrastText` 
      }}>
        <Box display="flex" alignItems="center" gap={1}>
          {actionType === 'checkin' ? <Login /> : <Logout />}
          {getActionTitle()}
        </Box>
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        {roomItem && (
          <>
            <Alert severity="info" sx={{ mb: 2 }}>
              {actionType === 'checkin' 
                ? `Confirm check-in for ${roomItem.room_name || 'this room'}.`
                : `Confirm check-out for ${roomItem.room_name || 'this room'}.`
              }
            </Alert>
            
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">Booking Details:</Typography>
              <Typography variant="body2" fontWeight="500">
                {roomItem.room_name || roomItem.description}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                <strong>Guest:</strong> {roomItem.customer_name}
              </Typography>
              <Typography variant="body2">
                <strong>Order:</strong> {roomItem.order_id}
              </Typography>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="subtitle2" color="text.secondary">Dates:</Typography>
              <Typography variant="body2">
                <strong>Check-In:</strong> {formatDate(roomItem.check_in_date)}
              </Typography>
              <Typography variant="body2">
                <strong>Check-Out:</strong> {formatDate(roomItem.check_out_date)}
              </Typography>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="subtitle2" color="text.secondary">Current Status:</Typography>
              <Chip
                label={roomItem.checked_out === 1 ? "Checked Out" : 
                       roomItem.checked_in === 1 ? "Checked In" : "Not Checked In"}
                color={roomItem.checked_out === 1 ? "info" : 
                       roomItem.checked_in === 1 ? "success" : "warning"}
                sx={{ mt: 1 }}
              />
            </Box>

            <TextField
              fullWidth
              multiline
              rows={3}
              label="Notes (Optional)"
              placeholder="Add any notes for this check-in/check-out..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              sx={{ mt: 2 }}
              disabled={loading}
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel 
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color={actionType === 'checkin' ? 'success' : 'warning'}
          startIcon={actionType === 'checkin' ? <Login /> : <Logout />}
          disabled={loading}
        >
          {loading ? <CircularProgress size={24} /> : getActionButtonText()}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const ViewDetailsDialog: React.FC<ViewDetailsDialogProps> = ({
  open,
  onClose,
  roomItem
}) => {
  const formatDate = (dateString: string) => {
    try {
      if (!dateString || dateString === "0000-00-00 00:00:00") {
        return "Not set";
      }
      return new Date(dateString).toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return "Invalid date";
    }
  };

  const formatDateShort = (dateString: string) => {
    try {
      if (!dateString || dateString === "0000-00-00 00:00:00") {
        return "Not set";
      }
      return new Date(dateString).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch (error) {
      return "Invalid date";
    }
  };

  const getStatusColor = () => {
    if (!roomItem) return 'default';
    if (roomItem.checked_out === 1) return 'info';
    if (roomItem.checked_in === 1) return 'success';
    return 'warning';
  };

  const getStatusText = () => {
    if (!roomItem) return '';
    if (roomItem.checked_out === 1) return 'Checked Out';
    if (roomItem.checked_in === 1) return 'Checked In';
    return 'Not Checked In';
  };

  const getStatusIcon = () => {
    if (!roomItem) return <Bedtime />;
    if (roomItem.checked_out === 1) return <Logout />;
    if (roomItem.checked_in === 1) return <Login />;
    return <Bedtime />;
  };

  if (!roomItem) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={2}>
            <Avatar sx={{ bgcolor: 'primary.main' }}>
              <Bed />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight="bold">
                {roomItem.room_name || roomItem.description}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Booking Details - Order #{roomItem.order_id}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent dividers sx={{ pt: 3 }}>
        <Grid container spacing={3}>
          {/* Left Column - Guest & Room Info */}
          <Grid item xs={12} md={6}>
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PersonPin /> Guest Information
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <Person />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Guest Name" 
                      secondary={
                        <Typography variant="body2" fontWeight="bold">
                          {roomItem.customer_name}
                        </Typography>
                      }
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <Phone />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Phone Number" 
                      secondary={
                        <Typography variant="body2">
                          {roomItem.customer_phone}
                        </Typography>
                      }
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <Email />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Email Address" 
                      secondary={
                        <Typography variant="body2">
                          {roomItem.customer_email}
                        </Typography>
                      }
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <BadgeIcon />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Date Created" 
                      secondary={
                        <Typography variant="body2" fontFamily="monospace">
                          {roomItem.created_at || 'N/A'}
                        </Typography>
                      }
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>

            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Room /> Room Information
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <Home />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Room Name" 
                      secondary={
                        <Typography variant="body2" fontWeight="bold">
                          {roomItem.room_name}
                        </Typography>
                      }
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <Description />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Description" 
                      secondary={
                        <Typography variant="body2">
                          {roomItem.description}
                        </Typography>
                      }
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <Business />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Room ID" 
                      secondary={
                        <Typography variant="body2" fontFamily="monospace">
                          {roomItem.product_id}
                        </Typography>
                      }
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <MeetingRoom />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Nights" 
                      secondary={
                        <Typography variant="body2">
                          {roomItem.quantity} night{roomItem.quantity > 1 ? 's' : ''}
                        </Typography>
                      }
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>

          {/* Right Column - Booking & Financial Info */}
          <Grid item xs={12} md={6}>
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Event /> Booking Details
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <CalendarToday />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Check-In Date" 
                      secondary={
                        <Typography variant="body2">
                          {formatDate(roomItem.check_in_date)}
                        </Typography>
                      }
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CalendarToday />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Check-Out Date" 
                      secondary={
                        <Typography variant="body2">
                          {formatDate(roomItem.check_out_date)}
                        </Typography>
                      }
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <DateRange />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Booked Date(s)" 
                      secondary={
                        <Typography variant="body2">
                          {(roomItem.date_bags)}
                        </Typography>
                      }
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <Schedule />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Booking Created" 
                      secondary={
                        <Typography variant="body2">
                          {formatDate(roomItem.created_at)}
                        </Typography>
                      }
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>

            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AttachMoney /> Financial Information
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <CreditCard />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Price per Night" 
                      secondary={
                        <Typography variant="body2">
                          {formatCurrencyN(roomItem.price)}
                        </Typography>
                      }
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <Receipt />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Total Amount" 
                      secondary={
                        <Typography variant="body2" fontWeight="bold" color="primary">
                          {formatCurrencyN(roomItem.total_amount)}
                        </Typography>
                      }
                    />
                  </ListItem>
                  {roomItem.cost_price && roomItem.cost_price !== "0" && (
                    <ListItem>
                      <ListItemIcon>
                        <AccountBalance />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Cost Price" 
                        secondary={
                          <Typography variant="body2" color="text.secondary">
                            {formatCurrencyN(parseFloat(roomItem.cost_price))}
                          </Typography>
                        }
                      />
                    </ListItem>
                  )}
                </List>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckCircle /> Status & Staff
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Chip
                        icon={getStatusIcon()}
                        label={getStatusText()}
                        color={getStatusColor()}
                        size="medium"
                        sx={{ height: 32 }}
                      />
                      <Chip
                        label={roomItem.status}
                        color={roomItem.status === 'COMPLETED' ? 'success' : 'default'}
                        variant="outlined"
                        size="medium"
                        sx={{ height: 32 }}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Processed By
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Person fontSize="small" />
                      <Typography variant="body2">
                        {roomItem.staff_name || 'Unknown Staff'}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Last Updated
                    </Typography>
                    <Typography variant="body2">
                      {formatDate(roomItem.last_updated)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      System Information
                    </Typography>
                    <Typography variant="caption" display="block">
                      Branch: {roomItem.branch_id.substring(0, 15)}...
                    </Typography>
                    <Typography variant="caption" display="block">
                      Business: {roomItem.business_id.substring(0, 15)}...
                    </Typography>
                    <Typography variant="caption" display="block">
                      Item ID: {roomItem.id}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </DialogContent>
      
      <DialogActions sx={{ p: 2, bgcolor: 'grey.50' }}>
        <Button 
          onClick={onClose} 
          variant="outlined"
          startIcon={<Close />}
        >
          Close
        </Button>
        <Button 
          variant="contained" 
          color="primary"
          startIcon={<Receipt />}
        >
          Print Receipt
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const RoomReservationsPage: React.FC = () => {
  const history = useHistory();
  const [roomItems, setRoomItems] = useState<RoomItem[]>([]);
  const [filteredItems, setFilteredItems] = useState<RoomItem[]>([]);
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState({
    reservations: false,
    checking: false,
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [checkDialog, setCheckDialog] = useState({
    open: false,
    roomItem: null as RoomItem | null,
    actionType: 'checkin' as 'checkin' | 'checkout',
  });
  const [viewDialog, setViewDialog] = useState({
    open: false,
    roomItem: null as RoomItem | null,
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error'
  });

  // Updated tabs array - REMOVED "Not Checked-In" tab
  const tabs = [
    { 
      label: "Checked-In", 
      icon: <Login />, 
      color: "success",
      description: "Rooms currently occupied by guests"
    },
    { 
      label: "Checked-Out", 
      icon: <Logout />, 
      color: "info",
      description: "Rooms where guests have completed their stay"
    },
  ];

  // Fetch data
  useEffect(() => {
    fetchRoomItems();
  }, []);

  useIonViewWillEnter(() => {
    fetchRoomItems();
  });

  // Filter logic - UPDATED for 2 tabs only
  useEffect(() => {
    if (!roomItems.length) {
      setFilteredItems([]);
      return;
    }

    let filtered = [...roomItems];
    
    // Filter by tab - UPDATED: Only 2 cases now
    if (activeTab === 0) { // Checked-In
      filtered = filtered.filter(item => item.checked_in === 1 && item.checked_out === 0);
    } else { // Checked-Out (activeTab === 1)
      filtered = filtered.filter(item => item.checked_out === 1);
    }

    // Apply search
    if (searchQuery.trim()) {
      const searchLower = searchQuery.toLowerCase();
      filtered = filtered.filter((item) => {
        return (
          item.customer_name.toLowerCase().includes(searchLower) ||
          item.room_name.toLowerCase().includes(searchLower) ||
          item.order_id.toLowerCase().includes(searchLower) ||
          item.customer_phone.toLowerCase().includes(searchLower) ||
          item.customer_email.toLowerCase().includes(searchLower)
        );
      });
    }

    setFilteredItems(filtered);
  }, [activeTab, searchQuery, roomItems]);

  const fetchRoomItems = async () => {
    try {
      setLoading((prev) => ({ ...prev, reservations: true }));
      
      const response = await getCustomersOrders_checkInCheckOut();
      console.log("Fetched room items:", response.data);
      
      if (response.data && Array.isArray(response.data)) {
        // Transform the API response to RoomItem format
        const transformedItems: RoomItem[] = response.data.map((item: any) => ({
          id: item.id?.toString() || '',
          order_id: item.order_id || '',
          product_id: item.product_id || '',
          quantity: Number(item.quantity) || 1,
          price: Number(item.price) || 0,
          total_amount: Number(item.total_amount) || 0,
          description: item.description || 'Room Booking',
          checked_in: Number(item.checked_in) || 0,
          checked_out: Number(item.checked_out) || 0,
          check_in_date: item.check_in_date || '',
          check_out_date: item.check_out_date || '',
          date_bags: item.date_bags || '',
          customer_name: item.customer_name || 'Unknown Guest',
          customer_phone: item.customer_phone || 'No phone',
          customer_email: item.customer_email || 'No email',
          staff_name: item.staff_name || '',
          status: item.status || '',
          created_at: item.created_at || '',
          last_updated: item.last_updated || '',
          member_id: item.member_id || '',
          branch_id: item.branch_id || '',
          business_id: item.business_id || '',
          room_name: item.room_name || item.description || 'Room',
          cost_price: item.cost_price,
          deleted_at: item.deleted_at,
          month_id: item.month_id,
          roleID: item.roleID,
          updated_at: item.updated_at,
        }));
        
        setRoomItems(transformedItems);
      } else {
        console.error("Unexpected response format:", response.data);
        setRoomItems([]);
      }
    } catch (error) {
      console.error("Error fetching room items:", error);
      setRoomItems([]);
    } finally {
      setLoading((prev) => ({ ...prev, reservations: false }));
    }
  };

  const handleCheckInOut = (roomItem: RoomItem, actionType: 'checkin' | 'checkout') => {
    setCheckDialog({
      open: true,
      roomItem,
      actionType,
    });
  };

  const handleViewDetails = (roomItem: RoomItem) => {
    setViewDialog({
      open: true,
      roomItem,
    });
  };

  const confirmCheckInOut = async (notes?: string) => {
    if (!checkDialog.roomItem) return;

    try {
      setLoading((prev) => ({ ...prev, checking: true }));
      
      const formData = new FormData();
      formData.append("requestID", requestID());
      formData.append("order_id", checkDialog.roomItem.order_id);
      formData.append("item_id", checkDialog.roomItem.id);
      formData.append("action_type", checkDialog.actionType);
      if (notes) {
        formData.append("notes", notes);
      }

      const response = await axios.post(
        baseUrl() + "reservations/check-in-out",
        formData,
        config
      );

      if (response.data.status === "success") {
        // Refresh the data immediately
        await fetchRoomItems();
        
        // Show success message
        setSnackbar({
          open: true,
          message: `${checkDialog.actionType === 'checkin' ? 'Check-in' : 'Check-out'} successful!`,
          severity: 'success'
        });
        
        setCheckDialog({ open: false, roomItem: null, actionType: 'checkin' });
      }
    } catch (error) {
      console.error(`Error:`, error);
      setSnackbar({
        open: true,
        message: `Error during ${checkDialog.actionType}`,
        severity: 'error'
      });
    } finally {
      setLoading((prev) => ({ ...prev, checking: false }));
    }
  };

  const handleCheckoutSuccess = async () => {
    try {
      // Refresh the data
      await fetchRoomItems();
      
      // Show success message
      setSnackbar({
        open: true,
        message: 'Check-out successful! Room has been checked out.',
        severity: 'success'
      });
      
      // If we're on the Checked-In tab, the room should disappear from the list
      // since it's now checked out
    } catch (error) {
      console.error("Error refreshing data after check-out:", error);
      setSnackbar({
        open: true,
        message: 'Check-out successful but failed to refresh data',
        severity: 'warning'
      });
    }
  };

  const formatDate = (dateString: string) => {
    try {
      if (!dateString || dateString === "0000-00-00 00:00:00") {
        return "Not set";
      }
      return new Date(dateString).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch (error) {
      return "Invalid date";
    }
  };

  const formatDateTime = (dateString: string) => {
    try {
      if (!dateString || dateString === "0000-00-00 00:00:00") {
        return "Not set";
      }
      return new Date(dateString).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return "Invalid date";
    }
  };

  // Calculate days between dates
  const getDaysBetween = (startDate: string, endDate: string) => {
    try {
      if (!startDate || startDate === "0000-00-00 00:00:00" || !endDate || endDate === "0000-00-00 00:00:00") {
        return 0;
      }
      const start = new Date(startDate);
      const end = new Date(endDate);
      const diffTime = Math.abs(end.getTime() - start.getTime());
      return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    } catch (error) {
      return 0;
    }
  };

  // Calculate totals
  const totalItems = filteredItems.length;
  const totalRevenue = filteredItems.reduce((sum, item) => sum + item.total_amount, 0);
  const checkedInCount = roomItems.filter(item => item.checked_in === 1).length;
  const checkedOutCount = roomItems.filter(item => item.checked_out === 1).length;

  const handleRefresh = () => {
    fetchRoomItems();
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <IonPage className="pushDown">
      <IonContent>
        {/* Pull to Refresh */}
        <IonRefresher
          slot="fixed"
          onIonRefresh={(e) => {
            fetchRoomItems().finally(() => e.detail.complete());
          }}
        >
          <IonRefresherContent />
        </IonRefresher>

        <Box sx={{ p: 3 }}>
          {/* Header with Refresh Button */}
          <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h4" gutterBottom>
                Room Reservations Management
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Manage room check-ins and check-outs
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<Refresh />}
              onClick={handleRefresh}
              disabled={loading.reservations}
            >
              {loading.reservations ? <CircularProgress size={20} /> : 'Refresh'}
            </Button>
          </Box>

          {/* Summary Cards - UPDATED for 3 cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={4}>
              <Card sx={{ bgcolor: 'primary.light', color: 'white' }}>
                <CardContent sx={{ textAlign: "center" }}>
                  <Hotel sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="h6">Total Rooms</Typography>
                  <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                    {roomItems.length}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card sx={{ bgcolor: 'success.light', color: 'white' }}>
                <CardContent sx={{ textAlign: "center" }}>
                  <Login sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="h6">Checked-In</Typography>
                  <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                    {checkedInCount}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card sx={{ bgcolor: 'info.light', color: 'white' }}>
                <CardContent sx={{ textAlign: "center" }}>
                  <Logout sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="h6">Checked-Out</Typography>
                  <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                    {checkedOutCount}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Tabs - Now shows only 2 tabs */}
          <Paper sx={{ mb: 3, borderRadius: 2 }}>
            <Tabs
              value={activeTab}
              onChange={(_, newValue) => setActiveTab(newValue)}
              variant="fullWidth"
              sx={{
                '& .MuiTab-root': {
                  py: 2,
                  fontSize: '0.95rem',
                  fontWeight: 600,
                }
              }}
            >
              {tabs.map((tab, index) => (
                <Tab 
                  key={index}
                  icon={tab.icon}
                  iconPosition="start"
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {tab.label}
                      <Badge 
                        badgeContent={roomItems.filter(item => {
                          if (index === 0) return item.checked_in === 1 && item.checked_out === 0;
                          return item.checked_out === 1;
                        }).length} 
                        color={tab.color as any}
                        sx={{ ml: 1 }}
                      />
                    </Box>
                  }
                />
              ))}
            </Tabs>
          </Paper>

          {/* Tab Description */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" fontWeight="600">
              {tabs[activeTab].label}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {tabs[activeTab].description}
            </Typography>
          </Box>

          {/* Search and Refresh */}
          <Box sx={{ 
            mb: 3, 
            display: "flex", 
            gap: 2, 
            alignItems: "center",
            p: 2,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 1
          }}>
            <TextField
              placeholder="Search by guest name, room name, order ID, phone or email..."
              variant="outlined"
              size="small"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: <Search />,
              }}
              sx={{ flexGrow: 1 }}
            />
            
            <Tooltip title="Refresh Data">
              <IconButton 
                onClick={handleRefresh} 
                disabled={loading.reservations}
                color="primary"
              >
                <Refresh />
              </IconButton>
            </Tooltip>
            
            <Typography variant="body2" color="text.secondary">
              Showing {filteredItems.length} of {roomItems.length} rooms
            </Typography>
          </Box>

          {/* Table */}
          {loading.reservations ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress />
            </Box>
          ) : filteredItems.length > 0 ? (
            <TableContainer component={Paper} elevation={2}>
              <Table>
                <TableHead>
                  <TableRow sx={{ 
                    backgroundColor: activeTab === 0 ? "success.main" : "info.main", 
                    color: 'white' 
                  }}>
                    <TableCell sx={{ fontWeight: 600, color: 'white' }}>Room & Booking Details</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'white' }}>Guest Information</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'white' }} align="center">Stay Period</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'white' }} align="right">Amount</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'white' }} align="center">Status</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'white' }} align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredItems.map((item) => {
                    const days = getDaysBetween(item.check_in_date, item.check_out_date);
                    const isCheckedIn = item.checked_in === 1;
                    const isCheckedOut = item.checked_out === 1;
                    
                    return (
                      <TableRow 
                        key={`${item.order_id}-${item.id}`}
                        sx={{
                          '&:hover': { backgroundColor: 'action.hover' }
                        }}
                      >
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                            <Avatar sx={{ 
                              bgcolor: isCheckedOut ? "info.main" : 
                                      isCheckedIn ? "success.main" : "warning.main",
                              width: 40, 
                              height: 40 
                            }}>
                              {isCheckedOut ? <Logout /> : 
                               isCheckedIn ? <Login /> : <Bed />}
                            </Avatar>
                            <Box>
                              <Typography variant="subtitle2" fontWeight="bold">
                                {item.room_name}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Order: {item.order_id}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" display="block">
                                {item.description}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" display="block">
                                Created: {formatDateTime(item.created_at)}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="500">
                            {item.customer_name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {item.customer_phone}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {item.customer_email}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Box>
                             <Typography variant="body2">
                              {(item.date_bags || '').split(' to ')[0]}
                            </Typography>
                            {days > 0 && (
                              <Chip
                                size="small"
                                label={`${days} night${days > 1 ? 's' : ''}`}
                                color="primary"
                                variant="outlined"
                                sx={{ mt: 1 }}
                              />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">
                            {formatCurrencyN(item.price)} × {item.quantity}
                          </Typography>
                          <Typography variant="body1" fontWeight="bold">
                            {formatCurrencyN(item.total_amount)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={isCheckedOut ? "Checked Out" : 
                                   isCheckedIn ? "Checked In" : "Not Checked In"}
                            color={isCheckedOut ? "info" : 
                                   isCheckedIn ? "success" : "warning"}
                            icon={isCheckedOut ? <Logout /> : 
                                  isCheckedIn ? <Login /> : <Bedtime />}
                          />
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                            {item.status}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Box display="flex" gap={1} justifyContent="center" flexWrap="wrap">
                            {/* Show Check-In button for rooms not checked in */}
                            {!isCheckedIn && !isCheckedOut && (
                              <Tooltip title="Check-In">
                                <Button
                                  variant="contained"
                                  color="success"
                                  size="small"
                                  startIcon={<Login />}
                                  onClick={() => handleCheckInOut(item, 'checkin')}
                                >
                                  Check-In
                                </Button>
                              </Tooltip>
                            )}
                            
                            {/* Show Check-Out button for rooms checked in but not checked out */}
                            {isCheckedIn && !isCheckedOut && (
                              <Tooltip title="Check-Out">
                                <Button
                                  variant="contained"
                                  color="warning"
                                  size="small"
                                  startIcon={<Logout />}
                                  onClick={() => handleCheckInOut(item, 'checkout')}
                                >
                                  Check-Out
                                </Button>
                              </Tooltip>
                            )}
                            
                            {/* View Details button for all */}
                            <Tooltip title="View Details">
                              <IconButton 
                                size="small" 
                                color="info"
                                onClick={() => handleViewDetails(item)}
                              >
                                <Visibility />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
                <TableFooter>
                  <TableRow sx={{ 
                    backgroundColor: activeTab === 0 ? "success.main" : "info.main", 
                    color: 'white' 
                  }}>
                    <TableCell colSpan={3} align="right" sx={{ fontWeight: 'bold', fontSize: '1.1rem', color: 'white' }}>
                      Totals ({totalItems} rooms)
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1.1rem', color: 'white' }}>
                      {formatCurrencyN(totalRevenue)}
                    </TableCell>
                    <TableCell colSpan={2} align="center" sx={{ fontWeight: 'bold', fontSize: '1.1rem', color: 'white' }}>
                      {activeTab === 0 ? "Currently Checked-In" : "Checked-Out Rooms"}
                    </TableCell>
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>
          ) : (
            <Paper sx={{ p: 4, textAlign: "center", borderRadius: 2 }}>
              {searchQuery ? (
                <>
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No rooms found matching your search
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Try adjusting your search terms
                  </Typography>
                  <Button 
                    variant="outlined" 
                    onClick={() => setSearchQuery("")}
                    startIcon={<Close />}
                  >
                    Clear Search
                  </Button>
                </>
              ) : loading.reservations ? (
                <CircularProgress />
              ) : (
                <>
                  {activeTab === 0 ? (
                    <Login sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} />
                  ) : (
                    <Logout sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} />
                  )}
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No {activeTab === 0 ? "checked-in" : "checked-out"} rooms
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {activeTab === 0 
                      ? "No rooms are currently checked in"
                      : "No rooms have been checked out yet"
                    }
                  </Typography>
                  <Button 
                    variant="contained" 
                    onClick={handleRefresh}
                    startIcon={<Refresh />}
                    sx={{ mt: 2 }}
                  >
                    Refresh Data
                  </Button>
                </>
              )}
            </Paper>
          )}
        </Box>

        {/* Check-In/Out Dialog */}
        <CheckInOutDialog
          open={checkDialog.open}
          onClose={() => setCheckDialog({ open: false, roomItem: null, actionType: 'checkin' })}
          roomItem={checkDialog.roomItem}
          onConfirm={confirmCheckInOut}
          loading={loading.checking}
          actionType={checkDialog.actionType}
          onSuccess={handleCheckoutSuccess}
        />

        {/* View Details Dialog */}
        <ViewDetailsDialog
          open={viewDialog.open}
          onClose={() => setViewDialog({ open: false, roomItem: null })}
          roomItem={viewDialog.roomItem}
        />

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert 
            onClose={handleCloseSnackbar} 
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </IonContent>
    </IonPage>
  );
};

export default RoomReservationsPage;